import { MPCIGridCarousel } from "./typings/iGridCarousel";
import { MPCLocalStorageService } from "../local-storage-service";
const SmartTimeout = require("smart-timeout");

export module MPGridColumnLock {
    import IGridCarousel = MPCIGridCarousel.IGridCarousel;
    import ProductComparisonModelManager = MPCLocalStorageService.ProductComparisonModelManager;

    export class GridColumnLock {
        private productComparisonModelManager: ProductComparisonModelManager;
        private readonly grid: HTMLElement;
        private readonly stickyHeader: HTMLElement;
        private currentLockedColumnIndex: number;
        private columnIsLocked: boolean;
        private readonly gridCarousel: IGridCarousel;
        private lockUnLockColumnEvent: EventListenerOrEventListenerObject;
        private lockInProgressTimeOut: number = 1000;
        private lockInProgress: boolean = false;

        constructor(scope: HTMLElement, gridCarousel: IGridCarousel) {
            this.grid = scope.querySelector('.grid');
            this.stickyHeader = scope.querySelector('.sticky-header-container');
            this.gridCarousel = gridCarousel;
            this.init();
        }

        private init(): void {
            this.currentLockedColumnIndex = 0;
            this.lockUnLockColumnEvent = (event: Event) => { this.handleLockUnlockColumnClick(event);}
            this.registerEvents();
            this.productComparisonModelManager = ProductComparisonModelManager.instance;

            if(!this.grid.classList.contains('empty-grid'))
                this.loadStoredLockedColumnInfo();
        }

        private registerEvents(): void {
            const columnHeaders: NodeListOf<HTMLElement> = this.grid.querySelectorAll('.column-header-container');
            const columnHeadersArray: Array<HTMLElement> = Array.prototype.slice.call(columnHeaders);

            for (const columnHeader of columnHeadersArray) {
                if(!columnHeader.querySelector('.header').classList.contains('empty-header')) {
                    columnHeader.querySelector('.mpc-locked-column').addEventListener('click', this.lockUnLockColumnEvent );
                }
            }
        }

        private loadStoredLockedColumnInfo(): void {
            // check locked column data:
            const lockedColumnIndex: number = this.productComparisonModelManager.getLockedColumnIndex();

            if (lockedColumnIndex) {
                let columnHeader: HTMLElement = this.getColumnHeader(lockedColumnIndex);

                if (columnHeader) {
                    const storedCarouselScrollIndex: number = this.productComparisonModelManager.getCarouselScrollIndex();

                    this.lockUnLockColumn(columnHeader.querySelector('.mpc-locked-column'), lockedColumnIndex);
                    
                    if(storedCarouselScrollIndex) {
                        this.gridCarousel.restorePreviousSlidePosition(storedCarouselScrollIndex);
                    }
                }
            }
        }

        private handleLockUnlockColumnClick(event: Event): void {
            const currentButtonContainer: HTMLElement = event.currentTarget as HTMLElement;
            const selectedColumnIndex: number = parseInt(currentButtonContainer.getAttribute('column-index'));
            this.lockUnLockColumn(currentButtonContainer, selectedColumnIndex);
        }

        private lockUnLockColumn(currentButtonContainer: HTMLElement, selectedColumnIndex: number): void {

            // avoid random fast lock unlock clicks:
            if (this.lockInProgress)
                return;

            // store selected column:
            let selectedColumn: Array<HTMLElement> = this.getColumn(selectedColumnIndex);

            // store sticky header slide:
            let selectedSlide: HTMLElement = this.getStickyHeaderSlide(selectedColumnIndex);

            // remove lock events:
            this.removeRegisteredEvents();

            // scroll grid to the primary position till the first column is visible on the left side:
            this.gridCarousel.swipeLeft();

            if(this.currentLockedColumnIndex == selectedColumnIndex) {
                this.changeLockIcon(currentButtonContainer.parentElement, false);
                this.changeLockIcon(selectedSlide, false);

                if(selectedColumnIndex > 1) {
                    // unlock columns and restore it primary positions:
                    this.unLockAndRestoreColumn(selectedColumn, selectedSlide);
                }
                else {
                    this.unlockColumn(selectedColumn, selectedSlide);
                }

                this.currentLockedColumnIndex = 0;
                this.columnIsLocked = false;

                // restore grid carousel size:
                this.gridCarousel.shrinkCarousel(false);

                // clear locked column info:
                this.productComparisonModelManager.clearLockedColumnIndex();
            }
            else {
                this.changeLockIcon(currentButtonContainer.parentElement, true);
                this.changeLockIcon(selectedSlide, true);

                // if user clicks lock on another product, but previous one was not unlocked yet:
                if(this.columnIsLocked) {
                    const previousLockedColumn: Array<HTMLElement> = this.getColumn(this.currentLockedColumnIndex);
                    const previousLockedSlide: HTMLElement = this.getStickyHeaderSlide(this.currentLockedColumnIndex);

                    const tmpColumnHeaderArray: Array<HTMLElement> = previousLockedColumn.filter(
                        it => it.classList.contains('column-header-container'));

                    this.changeLockIcon(tmpColumnHeaderArray[0].querySelector('.lock-unLock-container'), false);
                    this.changeLockIcon(previousLockedSlide, false);

                    if (this.currentLockedColumnIndex > 1) {
                        // unlock columns and restore it primary positions:
                        this.unLockAndRestoreColumn(previousLockedColumn, previousLockedSlide);
                    }
                    else {
                        this.unlockColumn(previousLockedColumn, previousLockedSlide);
                    }

                    this.columnIsLocked = false;

                    // re select selected column:
                    selectedColumn = this.getColumn(selectedColumnIndex);

                    // re sticky header slide:
                    selectedSlide = this.getStickyHeaderSlide(selectedColumnIndex);
                }

                if(selectedColumnIndex > 1) {
                    // lock column and replace it with first one:
                    this.lockAndMoveColumn(selectedColumn, selectedSlide, selectedColumnIndex);
                }
                else {
                    this.lockColumn(selectedColumn, selectedSlide);
                }

                // shrink grid carousel:
                this.gridCarousel.shrinkCarousel(true);

                this.currentLockedColumnIndex = selectedColumnIndex;
                this.columnIsLocked = true;

                // store locked column cookie info:
                this.productComparisonModelManager.storeLockedColumnIndex(selectedColumnIndex);
            }

            // register lock events again:
            this.registerEvents();
            this.lockInProgress = true;

            SmartTimeout.set(() => { this.lockInProgress = false; }, this.lockInProgressTimeOut);
        }

        private unLockAndRestoreColumn(lockedColumn: Array<HTMLElement>, lockedSlide: HTMLElement): void {
            let previousFirstSlideField: string;
            let lockedSlideTempField: string;

            // store first column, which takes places in locked column position:
            const previousFirstColumn: Array<HTMLElement> = this.getColumn(1);
            const previousFirstSlide: HTMLElement = this.getStickyHeaderSlide(1);
            const previousColumnIndex: number = this.getGridColumnIndex(lockedColumn[0]);

            for(let i: number = 0; i < previousFirstColumn.length; i++) {
                // unlock column:
                lockedColumn[i].classList.remove('locked-column');
                lockedColumn[i].classList.remove('grid-column-1');
                lockedColumn[i].classList.add('grid-column-' + previousColumnIndex);

                // rollback first column:
                previousFirstColumn[i].classList.remove('grid-column-' + previousColumnIndex);
                previousFirstColumn[i].classList.add('grid-column-1');
            }

            // unlock slide:
            lockedSlide.classList.remove('locked-column');

            previousFirstSlideField = previousFirstSlide.outerHTML;
            lockedSlideTempField = lockedSlide.outerHTML;

            // rollback slide data:
            previousFirstSlide.outerHTML = lockedSlideTempField;
            lockedSlide.outerHTML = previousFirstSlideField;
        }

        private lockAndMoveColumn(selectedColumns: Array<HTMLElement>, selectedSlide: HTMLElement, selectedColumnIndex: number): void {
            let firstSlideTempField: string;
            let selectedSlideTempField: string;
            const tmpFirstColumn: Array<HTMLElement> = this.getColumn(1);
            const tmpFirstSlide: HTMLElement = this.getStickyHeaderSlide(1);

            // replace selected column content with first column:
            for(let i: number = 0; i < tmpFirstColumn.length; i++) {
                // lock column:
                selectedColumns[i].classList.add('locked-column');

                // remove his own grid-column-??? class
                selectedColumns[i].classList.remove('grid-column-' + selectedColumnIndex);

                // move selected column to first position:
                selectedColumns[i].classList.add('grid-column-1')

                // remove his own grid-column-1 class
                tmpFirstColumn[i].classList.remove('grid-column-1');

                // move first column to selected position:
                tmpFirstColumn[i].classList.add('grid-column-' + selectedColumnIndex);
            }

            // replace selected slide content with first slide:
            selectedSlide.classList.add('locked-column');

            firstSlideTempField = tmpFirstSlide.outerHTML;
            selectedSlideTempField = selectedSlide.outerHTML;

            tmpFirstSlide.outerHTML = selectedSlideTempField;
            selectedSlide.outerHTML = firstSlideTempField;
        }

        private lockColumn(selectedColumns: Array<HTMLElement>, selectedSlide: HTMLElement): void {
            // lock defined column:
            for (const selectedColumn of selectedColumns) {
                selectedColumn.classList.add('locked-column');
            }

            // lock defined sticky header slide:
            selectedSlide.classList.add('locked-column');
        }

        private unlockColumn(selectedColumns: Array<HTMLElement>, selectedSlide: HTMLElement): void {
            // unlock column:
            for (const selectedColumn of selectedColumns) {
                selectedColumn.classList.remove('locked-column');
            }

            selectedSlide.classList.remove('locked-column');
        }

        private getColumn(gridColumnIndex: number): Array<HTMLElement> {
            const selectedColumn: NodeListOf<HTMLElement> = this.grid.querySelectorAll('[grid-column-index="'+ gridColumnIndex +'"]');
            return Array.prototype.slice.call(selectedColumn);
        }

        private getColumnHeader(gridColumnIndex: number): HTMLElement {
            const columnHeaders: NodeListOf<HTMLElement> = this.grid.querySelectorAll('.column-header-container');
            const columnHeadersArray: Array<HTMLElement> = Array.prototype.slice.call(columnHeaders);
            const columnHeader: HTMLElement = columnHeadersArray.filter(it => it.getAttribute('grid-column-index') == gridColumnIndex.toString())[0];

            return columnHeader;
        }

        private getStickyHeaderSlide(gridColumnIndex: number): HTMLElement {
            return this.stickyHeader.querySelector('[grid-column-index="' + gridColumnIndex + '"]');
        }

        private getGridColumnIndex(columnHeader: HTMLElement): number {
            return parseInt(columnHeader.getAttribute('grid-column-index'));
        }

        private removeRegisteredEvents(): void {
            const columnHeaders: NodeListOf<HTMLElement> = this.grid.querySelectorAll('.column-header-container');
            const columnHeadersArray: Array<HTMLElement> = Array.prototype.slice.call(columnHeaders);

            for (const columnHeader of columnHeadersArray) {
                const tmpLockedColumn: HTMLElement = columnHeader.querySelector('.mpc-locked-column');
                
                if(tmpLockedColumn)
                    tmpLockedColumn.removeEventListener('click', this.lockUnLockColumnEvent );
                
                const tmpUnLockedColumn: HTMLElement = columnHeader.querySelector('.mpc-locked-column');
                
                if(tmpUnLockedColumn)
                    tmpUnLockedColumn.removeEventListener('click', this.lockUnLockColumnEvent );
            }
        }

        private changeLockIcon(parentContainer: HTMLElement, showLocked: boolean): void {
            const lockedColumn: Element = parentContainer.querySelector('.mpc-locked-column');

            if (showLocked) {
                lockedColumn.classList.add('locked');
            }
            else {
                lockedColumn.classList.remove('locked');
            }
        }
    }
}